import { useState } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { api } from "@/lib/api";
import { CONTACT_EMAIL, SCHOLAR_URL } from "@/lib/constants";
import { Overline, Section } from "@/components/shared";
import { toast } from "sonner";
import { ArrowRight, Mail, GraduationCap } from "lucide-react";

const INQUIRY_TYPES = [
  "Independent research collaboration",
  "Research team consulting",
  "SME consulting",
  "Media / speaking",
  "Other",
];

const empty = { name: "", email: "", organization: "", inquiry_type: INQUIRY_TYPES[0], description: "", budget: "" };

export default function Contact() {
  useSeo(
    "Contact Systems Beyond",
    "Contact Systems Beyond for research collaboration, mathematical modeling, SME consulting, digital systems support or independent research inquiries."
  );

  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.description) {
      toast.error("Please complete name, email and description.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Inquiry sent. We'll be in touch shortly.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-transparent border-0 border-b border-silver focus:border-acid-dark outline-none py-3 text-ink placeholder:text-dark-gray/60 transition-colors";
  const labelCls = "overline block mb-1";

  return (
    <div>
      <Section className="pt-36 pb-16 lg:pt-44" grid>
        <div className="max-w-3xl">
          <Overline>Contact</Overline>
          <h1 className="mt-7 text-4xl lg:text-6xl tracking-tighter leading-[0.98] font-medium text-ink">Let's understand your system.</h1>
          <p className="mt-7 text-base lg:text-lg text-dark-gray leading-relaxed max-w-2xl">
            Tell us what you are working on — a research question, a biological model, a business
            challenge, or a possible collaboration.
          </p>
        </div>
      </Section>

      <Section className="pb-24 lg:pb-32">
        <div className="grid lg:grid-cols-12 gap-12">
          <form onSubmit={submit} className="lg:col-span-7 border border-silver/50 p-8 lg:p-12 bg-off-white" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <label className={labelCls}>Name</label>
                <input data-testid="contact-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" />
              </div>
              <div>
                <label className={labelCls}>Organization</label>
                <input data-testid="contact-organization" value={form.organization} onChange={set("organization")} className={inputCls} placeholder="Optional" />
              </div>
              <div>
                <label className={labelCls}>Budget / stage / urgency</label>
                <input data-testid="contact-budget" value={form.budget} onChange={set("budget")} className={inputCls} placeholder="Optional" />
              </div>
            </div>

            <div className="mt-8">
              <label className={labelCls}>Type of inquiry</label>
              <select data-testid="contact-inquiry-type" value={form.inquiry_type} onChange={set("inquiry_type")} className={`${inputCls} font-mono-sci text-sm cursor-pointer`}>
                {INQUIRY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div className="mt-8">
              <label className={labelCls}>Short description</label>
              <textarea data-testid="contact-description" value={form.description} onChange={set("description")} rows={5} className={`${inputCls} resize-none`} placeholder="Describe the system, question or challenge…" />
            </div>

            <button type="submit" disabled={submitting} data-testid="contact-submit" className="mt-10 inline-flex items-center gap-2 px-7 py-3.5 text-[13px] font-medium bg-acid text-charcoal hover:bg-acid-dark transition-all disabled:opacity-60 group">
              {submitting ? "Sending…" : "Send Inquiry"}
              <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </form>

          <div className="lg:col-span-5">
            <div className="border border-silver/50 p-8 bg-white">
              <p className="overline mb-6">Direct</p>
              <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-direct-email" className="flex items-center gap-3 py-3 text-ink hover:text-blue-gray transition-colors border-b border-silver/40">
                <Mail size={18} className="text-acid-dark" /><span className="text-sm">{CONTACT_EMAIL}</span>
              </a>
              <a href={SCHOLAR_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-scholar" className="flex items-center gap-3 py-3 text-ink hover:text-blue-gray transition-colors">
                <GraduationCap size={18} className="text-acid-dark" /><span className="text-sm">Google Scholar</span>
              </a>
            </div>
            <div className="border border-silver/50 p-8 bg-charcoal text-white mt-6">
              <p className="overline text-white/40 mb-4">Prefer a call?</p>
              <p className="text-white/70 text-sm leading-relaxed">
                Skip the form and book a time directly on our calendar — pick a slot that works and
                we'll meet you there.
              </p>
              <Link to="/book" data-testid="contact-book-call" className="mt-6 inline-flex items-center gap-2 px-6 py-3 text-[13px] font-medium bg-acid text-charcoal hover:bg-acid-dark transition-all group">
                Book a Call
                <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
